/**
 * Shared Error → PostHog property extractors used by both server and client
 * captureException paths. Keeps the two sides at parity so a `.cause` chain or
 * custom Error own-properties surface the same way regardless of where the
 * exception was caught.
 */

const STANDARD_ERROR_KEYS = new Set(["name", "message", "stack", "cause"]);

/**
 * Non-standard own properties attached to an Error (e.g. Stripe's .type/.code,
 * mailer's .email_template/.email_recipient). Skips standard Error fields so
 * we don't overwrite $exception_* or ship the stack twice.
 */
export function extractOwnProperties(err: Error): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.getOwnPropertyNames(err)) {
    if (STANDARD_ERROR_KEYS.has(key)) continue;
    out[key] = (err as unknown as Record<string, unknown>)[key];
  }
  return out;
}

/**
 * Walk Error.cause (drizzle wraps pg errors; some libs chain multiple levels).
 * Without this, all drizzle failures arrive in PostHog as generic
 * "Failed query: ..." messages with no way to tell a FK violation from a
 * NOT NULL violation.
 */
export function extractCauseChain(err: Error): Record<string, unknown> {
  const causes: Array<Record<string, unknown>> = [];
  let current: unknown = (err as { cause?: unknown }).cause;
  let depth = 0;
  while (current instanceof Error && depth < 5) {
    const c = current as Error & Record<string, unknown>;
    causes.push({
      name: c.name,
      message: c.message,
      code: c.code,
      constraint: c.constraint,
      detail: c.detail,
      table: c.table,
      column: c.column,
      schema: c.schema,
      severity: c.severity,
      routine: c.routine,
    });
    current = c.cause;
    depth += 1;
  }
  return causes.length > 0 ? { cause_chain: causes, cause: causes[0] } : {};
}
